/* eslint-disable array-callback-return */
/* eslint-disable consistent-return */
import React from 'react';
import CybsButton from '@cybs-ui/components/lib/button';
import shortid from 'shortid';

import PropTypes from 'prop-types';
import MoreActions from './moreActions';

const RightActionBar = ({
  actionBarId,
  dataTest,
  i18n: i18nProp,
  isActionBarActive,
  location,
  numberOfActionsToShow,
  onChange,
  rightActions
}) => {
  const i18n = {
    ...RightActionBar.defaultProps.i18n,
    ...i18nProp
  };

  const isElement = React.isValidElement(rightActions);

  let rightActionElement;
  // Fragile hack for a11y. TODO: find way to attach aria-describedby to all <button> tags
  if (isElement) {
    if (rightActions.type.displayName === 'CybsButton') {
      rightActionElement = React.cloneElement(rightActions, {
        'aria-describedby': actionBarId,
        'aria-controls': i18n.title && actionBarId
      });
    } else if (rightActions.type.displayName === 'MenuButton') {
      rightActionElement = React.cloneElement(rightActions, {
        buttonProps: {
          ...rightActions.props.buttonProps,
          'aria-describedby': actionBarId,
          'aria-controls': i18n.title && actionBarId
        }
      });
    } else {
      // Non-standard element. Change nothing.
      rightActionElement = React.cloneElement(rightActions);
    }
  }

  let actions;
  if (!isElement) {
    let shown = [...rightActions];
    let menuItems = [];
    if (Number.isInteger(numberOfActionsToShow)) {
      menuItems = shown.slice(numberOfActionsToShow);
      shown = shown.slice(0, numberOfActionsToShow);
    }
    actions = {
      shown,
      menuItems
    };
  }

  return (
    <React.Fragment>
      {isElement
        ? rightActionElement
        : Object.keys(actions).map(key => {
            if (key === 'shown') {
              return actions[key].map((buttonConfig, i) => (
                <CybsButton
                  aria-describedby={(i18n.title && actionBarId) || undefined}
                  aria-controls={i18n.title && actionBarId}
                  key={shortid.generate()}
                  dataTest={`right-button-${i}`}
                  {...buttonConfig}
                />
              ));
            }
            if (key === 'menuItems') {
              return (
                <MoreActions
                  actionBarId={actionBarId}
                  actions={actions.menuItems}
                  dataTest={`${dataTest}-more-actions`}
                  i18n={i18n}
                  isActive={isActionBarActive}
                  key={shortid.generate()}
                  onChange={onChange}
                  variant="icon"
                />
              );
            }
          })}
    </React.Fragment>
  );
};

RightActionBar.displayName = 'Actions';

RightActionBar.propTypes = {
  /**
   * Generated unique id
   */
  actionBarId: PropTypes.string.isRequired,
  /**
   * Actions on the right side of the bar. May pass array of button configs
   * or a React element.
   */
  rightActions: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(
      PropTypes.shape({
        ariaLabel: PropTypes.string,
        colorScheme: PropTypes.string,
        disabled: PropTypes.func,
        iconClass: PropTypes.string,
        iconLocation: PropTypes.string,
        href: PropTypes.string,
        label: PropTypes.string,
        onClick: PropTypes.func,
        onKeyDown: PropTypes.func,
        tag: PropTypes.string,
        variant: PropTypes.string
      })
    )
  ]),

  /**
   * Number of buttons to show before overflowing into menu.
   * */
  numberOfActionsToShow: PropTypes.number,
  /**
   * Action Bar for Grid proptypes
   */
  isActionBarActive: PropTypes.bool,
  /**
   * On Change is used for advanced dropdown menu for overflow Actions
   * */
  onChange: PropTypes.func,
  /**
   * Required data testing string
   */
  dataTest: PropTypes.string,

  /**
   * Strings for the component.
   */
  i18n: PropTypes.shape({
    collapsibleButtonLabel: PropTypes.string,
    collapsedLabel: PropTypes.string,
    expandedLabel: PropTypes.string,
    title: PropTypes.string.isRequired
  }),

  location: PropTypes.oneOf(['left', 'right'])
};

RightActionBar.defaultProps = {
  i18n: {
    collapsibleButtonLabel: 'Accordion toggle',
    collapsedLabel: 'Collapsed',
    expandedLabel: 'Expanded'
  },
  rightActions: [],
  numberOfActionsToShow: Infinity,
  isActionBarActive: true,
  onChange: () => {},
  dataTest: '',
  location: 'right'
};

export default RightActionBar;
